import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Resets scroll to the top of the document on every PUSH/REPLACE navigation.
 *
 * Rules honoured:
 * - Tell the browser to stop restoring scroll for PUSH/REPLACE (we handle it).
 * - Leave `history.scrollRestoration = 'auto'` behaviour for POP (back/forward),
 *   so the browser restores the previous scroll position as users expect.
 * - If the URL has a hash, scroll that element into view instead of the top.
 * - Instant scrolling on route change (no animation) — a smooth scroll from
 *   deep on the previous page to the top of the new page looks broken.
 * - `useLayoutEffect` + a `requestAnimationFrame` sequences the scroll so it
 *   fires after the new page's first paint, otherwise the browser can
 *   re-apply the outgoing position mid-transition.
 */
export function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType(); // "PUSH" | "REPLACE" | "POP"
  const isFirstRender = useRef(true);

  // Take over scroll restoration exactly once on mount.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      // We restore top on PUSH/REPLACE ourselves and delegate POP to the
      // browser's remembered position — the browser only reads
      // `scrollRestoration` on POP, so setting it to "auto" is what we want.
      window.history.scrollRestoration = "auto";
    }
  }, []);

  useEffect(() => {
    // Never fight the initial paint — the browser has already put us at the
    // correct spot (top for cold loads, remembered for reloads).
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Back/forward: let the browser restore the remembered offset.
    if (navType === "POP") return;

    // Wait one frame so the new page has laid out.
    const id = requestAnimationFrame(() => {
      if (hash) {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          // In-page anchors may animate smoothly if the user allows motion.
          const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
          el.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
          return;
        }
      }
      // Route changed: jump instantly to the top, never animate.
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
    return () => cancelAnimationFrame(id);
  }, [pathname, search, hash, navType]);

  return null;
}
